import clsx from 'clsx';
import * as React from 'react';
import { defaultSwatch } from '../../data/materials';
import { getTemplateRule } from '../../data/templateRules';
import { Draft } from '../services/Store';
import { Part, PartType, Swatch, TemplateRule } from '../types';
import useDraft from './useDraft';

const Parser = require('html-to-react').Parser;
const parser = new Parser();

const detection = {
    panel: (node: React.ReactElement) =>
        node.props.className &&
        (node.props.className.includes('panel') ||
            node.props.className.includes('fil0')),
    enforcment: (node: React.ReactElement) =>
        node.props.className &&
        (node.props.className.includes('enforcment') ||
            node.props.className.includes('fil1')),
    standoff: (node: React.ReactElement) =>
        node.props.className &&
        (node.props.className.includes('standoff') ||
            node.props.className.includes('str0')),
    rod: (node: React.ReactElement) =>
        node.props.className && node.props.className.includes('str2'),
    leadingEdge: (node: React.ReactElement) =>
        node.props.className &&
        (node.props.className.includes('leadingEdge') ||
            node.props.className.includes('str3')),
    upperSpreader: (node: React.ReactElement) =>
        node.props.className && node.props.className.includes('upperSpreader'),
    lowerSpreader: (node: React.ReactElement) =>
        node.props.className && node.props.className.includes('lowerSpreader'),
    misc: (node: React.ReactElement) =>
        node.props.className && node.props.className.includes('misc'),
};

const getPartType = (node): Part['type'] => {
    if (detection.panel(node)) return PartType.Panel;
    const detected = Object.values(PartType).find((key) => {
        return detection[key] && detection[key](node);
    });

    return (detected as Part['type']) || PartType.Misc;
};

const getPanelColor = (rule: TemplateRule, partData: Part): Swatch['value'] => {
    if (partData.type !== PartType.Panel) return;

    const panelRule =
        rule && rule.panels.find((item) => item.panelIds.includes(partData.id));

    if (panelRule) {
        return panelRule.swatch.value;
    }

    return (
        (partData.swatch && partData.swatch.value) ||
        partData.color ||
        defaultSwatch.value
    );
};

export function useKiteModel(
    svg: string,
    removeStyle?: boolean,
    simple?: boolean,
    uncolored?: boolean,
    onClick?: (part: Part) => void,
    onHover?: (part: Part) => void,
    selected?: Part
) {
    const { draft } = useDraft();
    if (!Object.keys(draft).length)
        return React.createElement('div', {}, 'no draft');

    const parts = [];

    const rawNode: React.ReactElement = (parser.parse(
        svg
    ) as React.ReactElement)[0];
    const isLegacy = rawNode.props.id !== 'mwd-colorizer';

    const { width, height, children, ...sanitizedSvgProps } = rawNode.props;

    const rule =
        getTemplateRule(draft.template, draft.variation || null) || null;

    const defs = children
        .filter((child) => typeof child === 'object')
        .filter((child) => child.type === 'defs');

    const sanitizedChildren = children
        .filter((child) => typeof child === 'object')
        .filter((child) => child.type === 'g')[0]
        .props.children.filter((child) => typeof child === 'object')
        .map((child, index) => {
            if (simple) {
                return React.cloneElement(child);
            }

            // NOTE : as defs, style, group and metadata were interpreted in the legacy version
            //  the index has to be adapted
            const legacyIndex = isLegacy ? index + 4 : index;

            // detect part type
            const partType = getPartType(child);

            // data from draft
            const partFromDraft =
                draft.parts &&
                draft.parts.length &&
                draft.parts.find((item) => item.id === legacyIndex);

            const partData: Part = partFromDraft
                ? partFromDraft
                : {
                      id: legacyIndex,
                      name: child.props.id || `${partType}-${legacyIndex}`,
                      type: partType,
                      swatch: defaultSwatch,
                  };
            parts.push(partData);

            const props = {
                ...child.props,
                style: {
                    fill: uncolored ? undefined : getPanelColor(rule, partData),
                },
                key: partData.id || `part-${legacyIndex}`,
                id: partData.id || `part-${legacyIndex}`,
                title: partData.name,
            };

            return React.cloneElement(child, {
                ...props,
                className: clsx(
                    [`kite__${partData.type}`, child.props.className, 'part'],
                    {
                        isSelected: selected && selected.id === partData.id,
                    }
                ),
                onClick: onClick
                    ? (event) => {
                          event.stopPropagation();
                          onClick(partData);
                      }
                    : undefined,
                onMouseOver: onHover ? onHover(partData) : undefined,
            });
        });

    if (!draft.parts || !draft.parts.length) {
        Draft.setParts(parts);
    }

    const partGroup = React.createElement('g', {
        key: 'part-group',
        children: sanitizedChildren,
    });

    return React.createElement('svg', {
        ...sanitizedSvgProps,
        key: draft.name,
        children: removeStyle ? partGroup : [defs, partGroup],
    });
}
